import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Download, Mail, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { getCheckoutStatus } from "@/lib/api";

export default function Success() {
  const { t, lang } = useLanguage();
  const { clear } = useCart();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.payment_status === "paid") {
          setData(res);
          setStatus("paid");
          clear();
          return;
        }
        if (res.status === "expired") {
          setStatus("failed");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 5) {
          setStatus("failed");
        } else {
          timer = setTimeout(poll, 2000);
        }
      }
    };

    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="bg-[#F9F8F6] min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-20 md:py-32 text-center">
        {status === "polling" && (
          <div data-testid="success-polling">
            <Loader2
              className="w-12 h-12 text-[#8BA896] mx-auto mb-6 animate-spin"
              strokeWidth={1.5}
            />
            <p className="font-heading text-2xl text-[#2C302E]">
              {t("success_processing")}
            </p>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="success-failed">
            <p className="font-heading text-2xl text-[#D97B66]">
              {t("success_failed")}
            </p>
            <Link
              to="/handlekurv"
              className="mt-6 inline-block bg-[#2C302E] text-white rounded-full px-8 py-3"
            >
              {lang === "en" ? "Back to cart" : "Tilbake til handlekurv"}
            </Link>
          </div>
        )}

        {status === "paid" && data && (
          <div data-testid="success-paid" className="fade-up">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#8BA896] flex items-center justify-center">
              <Check className="w-10 h-10 text-white" strokeWidth={2} />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium">
              {t("success_sub")}
            </p>
            <h1 className="mt-3 font-heading text-4xl md:text-6xl tracking-tight font-light text-[#2C302E]">
              {t("success_title")}
            </h1>

            <div className="mt-10 bg-white border border-[#E5E2DC] rounded-2xl p-6 md:p-8 text-left">
              <div className="flex items-center gap-3 text-sm text-[#6B726C] mb-5">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <span>{t("success_email")}</span>
                <span className="font-medium text-[#2C302E]" data-testid="customer-email">
                  {data.customer_email}
                </span>
              </div>

              <p className="font-heading text-lg text-[#2C302E] font-medium mb-4">
                {t("success_download")}
              </p>
              <ul className="space-y-5">
                {data.items?.map((it, i) => {
                  const itemTitle = lang === "en" ? it.title_en : it.title_no;
                  const downloads = it.downloads || [];
                  const isBundle = it.kind === "bundle";
                  return (
                    <li
                      key={i}
                      className="py-4 border-t border-[#E5E2DC] first:border-t-0"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {isBundle && (
                            <span className="bg-[#D97B66] text-white text-[10px] font-medium rounded-full px-2 py-0.5 uppercase tracking-wider">
                              Bundle
                            </span>
                          )}
                          <span className="text-[#2C302E] font-medium">{itemTitle}</span>
                        </div>
                      </div>
                      <ul className="space-y-2 pl-3 border-l-2 border-[#E5E2DC]">
                        {downloads.map((d, j) => {
                          const dtitle = lang === "en" ? d.title_en : d.title_no;
                          // Prefer tokenized download_url (secure), fall back to slug-only
                          const href = d.download_url
                            ? (d.download_url.startsWith("http")
                                ? d.download_url
                                : `${window.location.origin}${d.download_url}`)
                            : (d.slug
                                ? `${window.location.origin}/api/download/${d.slug}`
                                : "#");
                          return (
                            <li
                              key={j}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-[#6B726C]">{dtitle}</span>
                              <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`download-${d.slug}`}
                                className="inline-flex items-center gap-2 text-xs bg-[#8BA896] hover:bg-[#7A9685] text-white rounded-full px-3 py-1.5"
                              >
                                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                                PDF
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>

            <Link
              to="/butikk"
              data-testid="success-continue"
              className="mt-10 inline-block text-[#2C302E] hover:text-[#D97B66] underline underline-offset-4"
            >
              {t("success_continue")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
